import React, { createContext, Suspense, useCallback, useContext, useState } from 'react';
import dynamic from 'next/dynamic';
import { signalRService } from '@/services/signalR/SignalRService';
import { SignalRErrorBoundary } from '@/components/SignalR/SignalRErrorBoundary';
import type { SignalRConnectionSnapshot } from '@/providers/AppSignalRConnector';

const LazyAppSignalRConnector = dynamic(
  () => import('@/providers/AppSignalRConnector').then((m) => m.AppSignalRConnector),
  { ssr: false },
);

interface SignalRContextProps extends SignalRConnectionSnapshot {
  connect: () => Promise<void>;
  disconnect: () => Promise<void>;
}

const SIGNALR_CONTEXT_FALLBACK: SignalRContextProps = {
  loading: false,
  isConnected: false,
  connectionState: null,
  connectionId: null,
  connect: async () => {},
  disconnect: async () => {},
};

const SignalRContext = createContext<SignalRContextProps | undefined>(undefined);

interface Props {
  children: React.ReactNode;
}

export const AppSignalRProvider: React.FC<Props> = ({ children }) => {
  const [snapshot, setSnapshot] = useState<SignalRConnectionSnapshot>({
    loading: false,
    isConnected: false,
    connectionState: null,
    connectionId: null,
  });

  const handleConnectorUpdate = useCallback((partial: Partial<SignalRConnectionSnapshot>) => {
    setSnapshot((prev) => ({ ...prev, ...partial }));
  }, []);

  const updateConnectionState = useCallback(() => {
    setSnapshot((prev) => ({
      ...prev,
      isConnected: signalRService.isConnectionActive(),
      connectionState: signalRService.getConnectionState(),
      connectionId: signalRService.getConnectionId(),
    }));
  }, []);

  const connect = useCallback(async () => {
    setSnapshot((prev) => ({ ...prev, loading: true }));
    try {
      await signalRService.connect();
      updateConnectionState();
    } catch (error) {
      console.error('[AppSignalRProvider] Failed to connect:', error);
      updateConnectionState();
    } finally {
      setSnapshot((prev) => ({ ...prev, loading: false }));
    }
  }, [updateConnectionState]);

  const disconnect = useCallback(async () => {
    setSnapshot((prev) => ({ ...prev, loading: true }));
    try {
      await signalRService.disconnect();
      updateConnectionState();
    } catch (error) {
      console.error('[AppSignalRProvider] Failed to disconnect:', error);
      updateConnectionState();
    } finally {
      setSnapshot((prev) => ({ ...prev, loading: false }));
    }
  }, [updateConnectionState]);

  const value: SignalRContextProps = {
    ...snapshot,
    connect,
    disconnect,
  };

  return (
    <SignalRContext.Provider value={value}>
      {children}
      <SignalRErrorBoundary>
        <Suspense fallback={null}>
          <LazyAppSignalRConnector onUpdate={handleConnectorUpdate} />
        </Suspense>
      </SignalRErrorBoundary>
    </SignalRContext.Provider>
  );
};

export const useSignalRContext = () => {
  const context = useContext(SignalRContext);
  return context ?? SIGNALR_CONTEXT_FALLBACK;
};
